import React, { useState } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const InventoryTable = ({ items, onItemSelect, selectedItems, onQuantityUpdate, onItemEdit, onItemDelete }) => {
  const [editingQuantity, setEditingQuantity] = useState(null);
  const [tempQuantity, setTempQuantity] = useState('');

  const getStatusColor = (status) => {
    switch (status) {
      case 'in-stock':
        return 'bg-success/10 text-success border-success/20';
      case 'low-stock':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'out-of-stock':
        return 'bg-error/10 text-error border-error/20';
      case 'overstock':
        return 'bg-accent/10 text-accent border-accent/20';
      case 'discontinued':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'in-stock':
        return 'In Stock';
      case 'low-stock':
        return 'Low Stock';
      case 'out-of-stock':
        return 'Out of Stock';
      case 'overstock':
        return 'Overstock';
      case 'discontinued':
        return 'Discontinued';
      default:
        return 'Unknown';
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      onItemSelect(items?.map(item => item?.id));
    } else {
      onItemSelect([]);
    }
  };

  const handleItemSelect = (itemId, checked) => {
    if (checked) {
      onItemSelect([...selectedItems, itemId]);
    } else {
      onItemSelect(selectedItems?.filter(id => id !== itemId));
    }
  };

  const handleQuantityEdit = (itemId, currentQuantity) => {
    setEditingQuantity(itemId);
    setTempQuantity(currentQuantity?.toString());
  };

  const handleQuantitySave = (itemId) => {
    const newQuantity = parseInt(tempQuantity);
    if (!isNaN(newQuantity) && newQuantity >= 0) {
      onQuantityUpdate(itemId, newQuantity);
    }
    setEditingQuantity(null);
    setTempQuantity('');
  };

  const handleQuantityCancel = () => {
    setEditingQuantity(null);
    setTempQuantity('');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  const isAllSelected = items?.length > 0 && selectedItems?.length === items?.length;
  const isIndeterminate = selectedItems?.length > 0 && selectedItems?.length < items?.length;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 p-4">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="text-left p-4 font-medium text-foreground">Item Details</th>
              <th className="text-left p-4 font-medium text-foreground">SKU</th>
              <th className="text-left p-4 font-medium text-foreground">Category</th>
              <th className="text-left p-4 font-medium text-foreground">Current Stock</th>
              <th className="text-left p-4 font-medium text-foreground">Reorder Point</th>
              <th className="text-left p-4 font-medium text-foreground">Unit Price</th>
              <th className="text-left p-4 font-medium text-foreground">Supplier</th>
              <th className="text-left p-4 font-medium text-foreground">Status</th>
              <th className="text-left p-4 font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items?.map((item) => (
              <tr key={item?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                <td className="p-4">
                  <Checkbox
                    checked={selectedItems?.includes(item?.id)}
                    onChange={(e) => handleItemSelect(item?.id, e?.target?.checked)}
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                      <Icon name="Package" size={20} className="text-muted-foreground" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{item?.name}</div>
                      <div className="text-sm text-muted-foreground">{item?.description}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className="font-mono text-sm text-foreground">{item?.sku}</span>
                </td>
                <td className="p-4">
                  <span className="text-sm text-muted-foreground">{item?.category}</span>
                </td>
                <td className="p-4">
                  {editingQuantity === item?.id ? (
                    <div className="flex items-center space-x-2">
                      <Input
                        type="number"
                        value={tempQuantity}
                        onChange={(e) => setTempQuantity(e?.target?.value)}
                        className="w-20"
                        min="0"
                      />
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => handleQuantitySave(item?.id)}
                        iconName="Check"
                      />
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={handleQuantityCancel}
                        iconName="X"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-foreground">{item?.currentStock}</span>
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => handleQuantityEdit(item?.id, item?.currentStock)}
                        iconName="Edit2"
                      />
                    </div>
                  )}
                </td>
                <td className="p-4">
                  <span className="text-sm text-muted-foreground">{item?.reorderPoint}</span>
                </td>
                <td className="p-4">
                  <span className="font-medium text-foreground">{formatCurrency(item?.unitPrice)}</span>
                </td>
                <td className="p-4">
                  <span className="text-sm text-muted-foreground">{item?.supplier}</span>
                </td>
                <td className="p-4">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(item?.status)}`}>
                    {getStatusLabel(item?.status)}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-1">
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => onItemEdit(item?.id)}
                      iconName="Edit"
                      title="Edit item"
                    />
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => onItemDelete(item?.id)}
                      iconName="Trash2"
                      className="hover:text-error"
                      title="Delete item"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4 p-4">
        {items?.map((item) => (
          <div key={item?.id} className="bg-muted/30 rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <Checkbox
                  checked={selectedItems?.includes(item?.id)}
                  onChange={(e) => handleItemSelect(item?.id, e?.target?.checked)}
                />
                <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                  <Icon name="Package" size={20} className="text-muted-foreground" />
                </div>
                <div>
                  <div className="font-medium text-foreground">{item?.name}</div>
                  <div className="text-sm text-muted-foreground">{item?.sku}</div>
                </div>
              </div>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(item?.status)}`}>
                {getStatusLabel(item?.status)}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Stock:</span>
                <span className="ml-2 font-medium text-foreground">{item?.currentStock}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Reorder:</span>
                <span className="ml-2 font-medium text-foreground">{item?.reorderPoint}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Price:</span>
                <span className="ml-2 font-medium text-foreground">{formatCurrency(item?.unitPrice)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Supplier:</span>
                <span className="ml-2 font-medium text-foreground">{item?.supplier}</span>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-border">
              <span className="text-xs text-muted-foreground">{item?.category}</span>
              <div className="flex items-center space-x-1">
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => onItemEdit(item?.id)}
                  iconName="Edit"
                  title="Edit item"
                />
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => onItemDelete(item?.id)}
                  iconName="Trash2"
                  className="hover:text-error"
                  title="Delete item"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InventoryTable;